import { collection, doc, getDocs, setDoc, deleteDoc, updateDoc, getDoc, query, where } from 'firebase/firestore';
import { db } from '../config';
import { getUserById } from './userService';
import { getCurrentUserId } from '../../utils/auth';
import { getRoleById } from './roleService';
import { DocumentData } from 'firebase/firestore';

export interface Slip {
  id: string;
  x: number;
  y: number;
  // Optional relative positioning values (as percentages)
  relativeX?: number;
  relativeY?: number;
  // Slip number for display
  slipNumber?: string;
  // Customer reference
  customerId?: string;
  // Renter information fields
  renterName?: string;
  boatName?: string;
  boatType?: string;
  phoneNumber?: string;
  leaseEndDate?: string;
  // Vessel assignment
  assignedVesselId?: string;
  // Add more fields as needed (e.g., status, etc)
  status: 'available' | 'occupied' | 'reserved' | 'maintenance';
  type: 'standard' | 'premium' | 'oversized';
  length: number;
  width: number;
  depth: number;
  dock: string;
  notes?: string;
  currentVessel?: string;
  currentCustomer?: string;
  assignedTo?: string; // Customer ID of the assigned customer
  lastMaintenance?: Date;
  nextMaintenance?: Date;
  createdAt: Date;
  updatedAt: Date;
  // Contract and payment status
  contractId?: string;
  paymentStatus?: 'paid' | 'pending' | 'overdue';
  // Launch status
  launchStatus?: 'not_launched' | 'launched' | 'completed';
}

// Convert absolute to relative position (for storing)
export const toRelativePosition = (x: number, y: number, containerWidth: number, containerHeight: number): { relativeX: number, relativeY: number } => {
  const relativeX = (x / containerWidth) * 100;
  const relativeY = (y / containerHeight) * 100;
  return { relativeX, relativeY };
};

// Convert relative to absolute position (for rendering)
export const toAbsolutePosition = (relativeX: number, relativeY: number, containerWidth: number, containerHeight: number): { x: number, y: number } => {
  const x = (relativeX / 100) * containerWidth;
  const y = (relativeY / 100) * containerHeight;
  return { x, y };
};

const COLLECTION_NAME = 'slips';

// Helper function to verify user role
const verifyUserRole = async () => {
  const userId = getCurrentUserId();
  if (!userId) {
    throw new Error('User not authenticated');
  }
  
  const userProfile = await getUserById(userId);
  if (!userProfile) {
    throw new Error('User profile not found');
  }
  
  const role = await getRoleById(userProfile.roleId);
  if (!role || role.name !== 'admin') {
    throw new Error('User does not have required role (admin)');
  }
  
  return userProfile;
};

export const getAllSlips = async (): Promise<Slip[]> => {
  try {
    // Reading slips is allowed for multiple roles
    const snapshot = await getDocs(collection(db, COLLECTION_NAME));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Slip));
  } catch (error) {
    console.error('Error getting slips:', error);
    throw error;
  }
};

export const upsertSlip = async (slip: Slip): Promise<void> => {
  try {
    // Verify user role before proceeding
    await verifyUserRole();
    
    await setDoc(doc(db, COLLECTION_NAME, slip.id), slip, { merge: true });
  } catch (error) {
    console.error('Error upserting slip:', error);
    throw error;
  }
};

export const deleteSlip = async (id: string): Promise<void> => {
  try {
    // Verify user role before proceeding
    await verifyUserRole();
    
    await deleteDoc(doc(db, COLLECTION_NAME, id));
  } catch (error) {
    console.error('Error deleting slip:', error);
    throw error;
  }
};

export const getSlipById = async (slipId: string) => {
  try {
    const slipRef = doc(db, 'slips', slipId);
    const slipDoc = await getDoc(slipRef);
    if (!slipDoc.exists()) {
      return null;
    }
    return {
      id: slipDoc.id,
      ...slipDoc.data()
    } as Slip;
  } catch (error) {
    console.error('Error getting slip:', error);
    throw error;
  }
};

interface SlipCriteria {
  minLength?: number;
  minWidth?: number;
  type?: string;
}

export const getAvailableSlips = async (criteria: SlipCriteria): Promise<Slip[]> => {
  try {
    const slipsRef = collection(db, 'slips');
    const q = query(slipsRef, where('status', '==', 'available'));

    const querySnapshot = await getDocs(q);
    const slips = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Slip[];

    // Filter slips based on criteria
    return slips.filter(slip => {
      if (criteria.minLength && slip.length < criteria.minLength) return false;
      if (criteria.minWidth && slip.width < criteria.minWidth) return false;
      if (criteria.type && slip.type !== criteria.type) return false;
      return true;
    });
  } catch (error) {
    console.error('Error getting available slips:', error);
    throw error;
  }
}; 